import React, { useState, useEffect } from 'react';
import { StyleSheet, ScrollView, Platform, View, ActivityIndicator } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { PrivateStackParamList } from '@routes/types';
import { ThemedText, ThemedView, Theme } from '@app/theme';
import ROUTE from '@app/navigationApp/routes';
import ScreenWrapper from '@app/hoc/ScreenWrapper';
import CommonHeader from '@app/sharedComponents/Header/Header';
import Stepper from '@app/sharedComponents/Stepper/Stepper';
import { useStepperData } from '@app/hooks/useStepperData';
import Button from '@app/sharedComponents/Button/Button';
import { useTheme } from '@shopify/restyle';
import InputText from '@app/sharedComponents/TextInput/TextInput';
import GoogleLogin from 'react-google-login';
import { GoogleSignin } from '@react-native-google-signin/google-signin';
import GoogleIcon from '../../../assets/Icons/GoogleIcon';
import { trackEvent } from '@app/utils/amplitude';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import UploadIllustration from '@app/assets/svg/UploadIllustration';
import { CheckCircle } from '@app/sharedComponents/Icons';
import axios from 'axios';
import { endpoints } from '@app/api/apis';
import { showErrorSheet } from '@app/store/slices/errorSlice';
import VerifyEmailOtp, { RESPONSE_STATUS } from './components/VerifyEmailOtp';
import { getApplicationId } from '../FetchPortfolio/utils';
import BottomSheetContainer from '@app/sharedComponents/Bottomsheet/Bottomsheet';
import gs from '../../styles/globalStyles';
import useIsMobileBrowser from '@app/hooks/useIsMobileBrowser';
import { updateLinkedApplication, updateUserDetailEmail } from '@app/store/slices/authSlice';
import { getNextRoute } from '@app/navigationApp/navigationUtil';
import { LENDER_ID } from '@app/types/regix';
import TataLogo from '../../../assets/Icons/TataLogo';
import DSPLogoNew from '@app/assets/svg/DSPLogoNew';
import UseIsKeyboardOpen from '@app/hooks/useIsKeyBoardOpen';
import LottieView from 'lottie-react-native';

const clientID = '62646021413-queb1g13go0snvnotl0ee06t68jcgb98.apps.googleusercontent.com';
const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const GoogleButton = (props: any) => {
    return (
        <Button
            eventName='VERIFY_EMAIL_WITH_GOOGLE'
            size='MD'
            type="OUTLINED"
            label="Continue with Google"
            handlePress={props?.onPress}
            showLeftAccessory
            leftAccessory={() => {
                return <GoogleIcon />;
            }}
        />
    );
};

type Props = NativeStackScreenProps<PrivateStackParamList, typeof ROUTE.EMAIL_VERIFICATION>;

enum MODAL_TYPE {
    OTP = 'OTP',
    SUCCESS = 'SUCCESS',
    NONE = 'NONE'
}

const EmailVerification = ({ navigation, route }: Props) => {
    const { stepperData, isLoading: isStepperLoading } = useStepperData();
    const { userDetails } = useAppSelector(state => state.auth);
    const { colors } = useTheme<Theme>();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [buttonLoader, setButtonLoader] = useState(false);
    const [otpDigitCount, setOtpDigitCount] = useState(0);
    const dispatch = useAppDispatch();
    const isMobileBrowser = useIsMobileBrowser();
    const [activeModal, setActiveModal] = useState<MODAL_TYPE>(MODAL_TYPE.NONE);
    const [successData, setSuccessData] = useState<any>(null);
    const [enableGoogleSSO, setEnableGoogleSSO] = useState(true);
    const [isHydrating, setIsHydrating] = useState(true);
    const { isKeyboardOpen } = UseIsKeyboardOpen();
    const [successLottie, setSuccessLottie] = useState<any>(null);

    const handleSuccessNavigation = () => {
        setActiveModal(MODAL_TYPE.NONE);
        if (successData?.updatedApplicationObj) {
            const { updatedApplicationObj } = successData;
            dispatch(updateLinkedApplication(updatedApplicationObj));
            const nextRoute = getNextRoute(
                updatedApplicationObj.currentStepId,
                updatedApplicationObj?.applicationState,
                updatedApplicationObj?.stepStatusMap
            );
            navigation.reset({
                index: 0,
                routes: [
                    {
                        name: ROUTE.KYC_STEPPER,
                    },
                    {
                        name: nextRoute
                    }
                ]
            })
        }
    };

    const fetchSuccessLottie = async () => {
        try {
            const response = await fetch("https://lottie.host/34e6bfb8-a91f-4e4f-8856-7571d41cee8e/6sPdPdx4Yf.json", {
                method: "GET",
            })
                .then((response) => response.json())
                .then((responseData) => {
                    setSuccessLottie(responseData);
                })
                .catch((error) => {
                    console.error(error);
                })
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        Promise.all([fetchSuccessLottie()]);
        let timer: NodeJS.Timeout;
        if (activeModal === RESPONSE_STATUS.SUCCESS) {
            timer = setTimeout(() => {
                handleSuccessNavigation();
            }, 3000);
        }
        return () => clearTimeout(timer);
    }, [activeModal, successData]);

    const fetchEmailVerificationDetails = async () => {
        try {
            const applicationId = await getApplicationId(userDetails?.linkedApplications[0]?.applicationId);
            const res = await axios.get(`${endpoints.getEmailVerificationDetails}${applicationId}`);
            if (res?.data) {
                if (res?.data?.emailId) {
                    setEmail(res?.data?.emailId);
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    setIsEmailValid(emailRegex.test(res?.data?.emailId));
                }
                if (res?.data?.enableGoogleSSO !== undefined) {
                    setEnableGoogleSSO(res.data.enableGoogleSSO);
                }
            }
        } catch (error) {
            console.warn('Error fetching email verification details', error);
        } finally {
            setIsHydrating(false);
        }
    };

    useEffect(() => {
        fetchEmailVerificationDetails();
        if (Platform.OS !== 'web') {
            GoogleSignin.configure({});
        }
    }, []);

    const onInput = (text: string) => {
        const cleanedText = text.replace(/\s/g, '');
        setEmail(cleanedText);
        const isValid = emailRegex.test(cleanedText);
        setIsEmailValid(isValid);
        if (error) setError('');
    };

    const onFocus = () => {
        setError('');
    };

    const onBlur = () => {
        if (email.length > 0 && !isEmailValid) {
            setError('Enter a valid email address');
        }
    };

    const onResponse = async (response: any) => {
        if (response.error) return;
        const googleRecievedEmailId = Platform.select({
            native: response?.user?.email,
            default: response?.profileObj?.email,
        });
        trackEvent('EMAIL_VERIFIED', { email: googleRecievedEmailId });
        setButtonLoader(true);
        const applicationId = await getApplicationId(userDetails?.linkedApplications[0]?.applicationId);
        const payload = {
            email: googleRecievedEmailId,
            applicationId: applicationId
        };

        try {
            const res = await axios.post(endpoints.googleEmailUpdateAttribute, payload);
            if (res.data === RESPONSE_STATUS.SUCCESS || res?.data?.status === RESPONSE_STATUS.SUCCESS) {
                setSuccessData(res.data);
                setActiveModal(MODAL_TYPE.SUCCESS);
                dispatch(updateUserDetailEmail({ emailId: googleRecievedEmailId }));
            }
        } catch (error: any) {
            dispatch(
                showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: error?.response?.data?.message || 'Something went wrong',
                    closeOnBackdropPress: true,
                    errorType: error?.response?.data?.voltErrorCode || error?.response?.data?.statusCode,
                    routeName: route.name
                })
            );
        } finally {
            setButtonLoader(false);
        }
    };

    const mobileSignIn = async () => {
        try {
            await GoogleSignin.signOut();
            await GoogleSignin.hasPlayServices();
            const userInfo = await GoogleSignin.signIn();
            await onResponse(userInfo);
        } catch (error) {
            console.warn('Google error-> ', error);
        }
    };

    const handleVerify = async () => {
        setButtonLoader(true);
        const applicationId = await getApplicationId(userDetails?.linkedApplications[0]?.applicationId);
        const payload = {
            emailId: email,
            applicationId: applicationId
        };

        try {
            const response = await axios.post(endpoints.emailInit, payload);
            if (response.data.status === RESPONSE_STATUS.SUCCESS) {
                // console.log('OTP sent successfully', response.data);
                setOtpDigitCount(response.data.noOfDigits || 6);
                setActiveModal(MODAL_TYPE.OTP);
            }
        } catch (error: any) {
            dispatch(
                showErrorSheet({
                    isErrorSheetVisible: true,
                    errorMessage: error?.data?.message || 'Something went wrong',
                    closeOnBackdropPress: true,
                    errorType: error?.data?.voltErrorCode || error?.data?.statusCode,
                    routeName: route.name
                })
            );
        } finally {
            setButtonLoader(false);
        }
    }

    const onVerifySuccess = (data: any) => {
        setOtpDigitCount(0);
        setSuccessData(data);
        setActiveModal(MODAL_TYPE.SUCCESS);
        dispatch(updateUserDetailEmail({ emailId: email }));
    };

    const handleModalClose = () => {
        if (activeModal === MODAL_TYPE.SUCCESS) {
            handleSuccessNavigation();
        } else {
            setActiveModal(MODAL_TYPE.NONE);
            setOtpDigitCount(0);
        }
    };

    return (
        <ScreenWrapper screenName={ROUTE.EMAIL_VERIFICATION}>
            <ThemedView flex={1} bg='bg-surface-primary'>
                <CommonHeader
                    label='Enter email'
                    navigation={navigation}
                    hideBottomBorder
                    pageName={route.name}
                    customGoBackAction={() => {
                        navigation.navigate(ROUTE.KYC_STEPPER)
                    }}
                />
                <Stepper isLoading={isStepperLoading} stepperData={stepperData} />
                {isHydrating ? (
                    <ThemedView flex={1} justifyContent='center' alignItems='center'>
                        <ActivityIndicator size="small" color={colors['text-action-primary']} />
                    </ThemedView>
                ) : (
                    <>
                            <ScrollView contentContainerStyle={{ flexGrow: 1 }}>
                            <ThemedView style={styles.container}>
                                <UploadIllustration size={64} />
                                <ThemedText variant='h5s' color='text-primary' style={styles.title} >Verify email to receive agreement</ThemedText>
                                <ThemedText variant='b3r' color='text-secondary' style={styles.subtitle}>This will be your main email id for all official loan related communication</ThemedText>
                                {enableGoogleSSO && (
                                    <ThemedView marginBottom='md'>
                                        {Platform.OS === 'web' ? (
                                            <GoogleLogin
                                                clientId={clientID}
                                                render={renderProps => (
                                                    <GoogleButton onPress={renderProps.onClick} />
                                                )}
                                                buttonText="Login"
                                                onSuccess={onResponse}
                                                onFailure={onResponse}
                                                cookiePolicy={'single_host_origin'}
                                                isSignedIn={false}
                                            />
                                        ) : (
                                            <GoogleButton onPress={mobileSignIn} />
                                        )}
                                    </ThemedView>
                                )}

                                {enableGoogleSSO && (
                                    <ThemedView style={styles.orContainer} marginBottom='sm'>
                                        <ThemedView style={[styles.line, { backgroundColor: colors['border-primary'] }]} />
                                        <ThemedText variant='b3r' style={styles.orText} color='text-secondary'>OR</ThemedText>
                                        <ThemedView style={[styles.line, { backgroundColor: colors['border-primary'] }]} />
                                    </ThemedView>
                                )}

                                <InputText
                                    label="Enter email"
                                    value={email}
                                    onChangeText={onInput}
                                    onFocus={onFocus}
                                    onBlur={onBlur}
                                    keyboardType="email-address"
                                    autoFocus={true}
                                    showLabel
                                    showErrorMessage={true}
                                    hasError={error}
                                    showRightAccessory={true}
                                    rightAccessory={() => {
                                        return (
                                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                                {isEmailValid && (
                                                    <CheckCircle
                                                        size={24}
                                                        weight="fill"
                                                        fill={colors['icon-success']}
                                                    />
                                                )}
                                            </View>
                                        );
                                    }}
                                />
                            </ThemedView>
                        </ScrollView>
                            {
                                (Platform.OS === 'web' || !isKeyboardOpen) && 
                                <ThemedView
                                    bg='bg-surface-primary'
                                    padding='lg'
                                    borderTopWidth={1}
                                    borderTopColor='border-primary'
                                >
                                    <Button
                                        label="Verify"
                                        type="SOLID"
                                        size='MD'
                                        handlePress={handleVerify}
                                        disabled={!isEmailValid}
                                        buttonLoader={buttonLoader}
                                    />
                                        <ThemedView flexDirection='row' alignItems='center' justifyContent='center' marginTop='sm'>
                                            <ThemedView flexDirection='row' alignItems='center' justifyContent='center'>
                                                <ThemedText color='text-secondary' variant='b5m' paddingRight='sm'>
                                                    LENDING PARTNER
                                                </ThemedText>
                                            </ThemedView>
                                            {
                                                userDetails?.linkedApplications[0]?.lenderAccountId?.toLowerCase() === LENDER_ID.TATA.toLowerCase()
                                                    ? <ThemedView>
                                                        <TataLogo />
                                                    </ThemedView>
                                                    : userDetails?.linkedApplications[0]?.lenderAccountId?.toLowerCase() === LENDER_ID.DSP.toLowerCase()
                                                        ? <ThemedView>
                                                            <DSPLogoNew width={35} height={25} color={colors['icon-primary']} />
                                                        </ThemedView>
                                                        : <></>
                                            }

                                        </ThemedView>
                                </ThemedView>
                            }

                    </>
                )}
                <BottomSheetContainer
                    isVisible={activeModal !== MODAL_TYPE.NONE}
                    onClose={handleModalClose}
                    close={handleModalClose}
                    closeOnBackdropPress={false}
                    borderTopRightRadius={10}
                    borderTopLeftRadius={10}
                    showCloseIcon={activeModal === MODAL_TYPE.SUCCESS ? false : true}
                    //@ts-expect-error
                    containerStyle={[gs.setBg('#fefefe'), {
                        maxHeight: activeModal === MODAL_TYPE.OTP ? '80%' : undefined,
                        minHeight: isMobileBrowser ? 330 : undefined,
                    }]}
                >
                    {activeModal === MODAL_TYPE.OTP && (
                        <ThemedView>
                            <VerifyEmailOtp
                                email={email}
                                applicationId={userDetails?.linkedApplications[0]?.applicationId}
                                otpDigitCount={otpDigitCount}
                                onClose={handleModalClose}
                                onVerifySuccess={onVerifySuccess}
                            />
                            {Platform.OS === 'android' && (
                                <ThemedView height={10} />
                            )}
                        </ThemedView>
                    )}
                    {activeModal === MODAL_TYPE.SUCCESS && (
                        <ThemedView padding='lg' alignItems='center'>
                            <ThemedView marginTop='md' />
                            <LottieView source={successLottie} autoPlay loop style={{ width: 70, height: 70 }} />
                            <ThemedText variant='h4s' marginTop='2xl' textAlign='center'>Email verified successfully</ThemedText>
                            <ThemedText variant='b3r' color='text-secondary' marginTop='md' textAlign='center' marginBottom='2xl'>
                                If you need this email will be editable from the profile section on the app. You will be redirected to next step in few seconds.
                            </ThemedText>
                            <Button
                                label="Continue"
                                type="SOLID"
                                size="MD"
                                handlePress={handleSuccessNavigation}
                            />
                        </ThemedView>
                    )}
                </BottomSheetContainer>
            </ThemedView>
        </ScreenWrapper>
    );
};

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: 16,
        paddingVertical: 24
    },
    title: {
        marginBottom: 8,
        marginTop: 8
    },
    subtitle: { // Hardcoded as per temporary rule
        marginBottom: 24,
    },
    orContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    line: {
        flex: 1,
        height: 1,
        // backgroundColor: '#E0E0E0',
    },
    orText: {
        marginHorizontal: 10,
    },
});

export default EmailVerification;
